/**
 * Format a number as currency
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: USD)
 * @param {string} locale - Locale string (default: en-US)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
	}).format(amount)
}

/**
 * Format a date
 * @param {Date|string|number} date - The date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @param {string} locale - Locale string (default: en-US)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}, locale = 'en-US') => {
	const dateObj = date instanceof Date ? date : new Date(date)
	const defaultOptions = {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		...options,
	}
	return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj)
}

/**
 * Format a relative time (e.g., "2 hours ago")
 * @param {Date|string|number} date - The date to compare
 * @param {string} locale - Locale string (default: en-US)
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date, locale = 'en-US') => {
	const dateObj = date instanceof Date ? date : new Date(date)
	const now = new Date()
	const diffInSeconds = Math.floor((now - dateObj) / 1000)

	const intervals = [
		{ seconds: 31536000, unit: 'year' },
		{ seconds: 2592000, unit: 'month' },
		{ seconds: 86400, unit: 'day' },
		{ seconds: 3600, unit: 'hour' },
		{ seconds: 60, unit: 'minute' },
		{ seconds: 1, unit: 'second' },
	]

	for (const interval of intervals) {
		const count = Math.floor(diffInSeconds / interval.seconds)
		if (count >= 1) {
			const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
			return rtf.format(-count, interval.unit)
		}
	}

	return 'just now'
}

/**
 * Capitalize first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} Capitalized string
 */
export const capitalize = str => {
	if (!str || typeof str !== 'string') return ''
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Convert string to title case
 * @param {string} str - The string to convert
 * @returns {string} Title case string
 */
export const titleCase = str => {
	if (!str || typeof str !== 'string') return ''
	return str
		.toLowerCase()
		.split(' ')
		.map(word => capitalize(word))
		.join(' ')
}

/**
 * Truncate string with ellipsis
 * @param {string} str - The string to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @param {string} suffix - Suffix to add (default: '...')
 * @returns {string} Truncated string
 */
export const truncate = (str, maxLength, suffix = '...') => {
	if (!str || typeof str !== 'string') return ''
	if (str.length <= maxLength) return str
	return str.slice(0, maxLength - suffix.length) + suffix
}

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes, decimals = 2) => {
	if (bytes === 0) return '0 Bytes'

	const k = 1024
	const dm = decimals < 0 ? 0 : decimals
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']

	const i = Math.floor(Math.log(bytes) / Math.log(k))

	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}
